import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiClientError } from "@/lib/api-client";
import type { ConfirmationResult } from "@/hooks/use-assistant";

/** The subset of the useRespondConfirmation mutation the card consumes. */
export interface RespondMutationLike {
  mutateAsync: (input: {
    messageId: string;
    confirmationId: string;
    action: "confirm" | "dismiss";
  }) => Promise<ConfirmationResult>;
  isPending: boolean;
}

export interface ConfirmationCardProps {
  messageId: string;
  confirmationId: string;
  verb: string;
  object: string;
  consequence: string;
  respond: RespondMutationLike;
  /** Persisted records can render the card preresolved. */
  initialStatus?: "pending" | "confirmed" | "dismissed" | "expired";
  initialSummary?: string;
}

type Resolution =
  | { kind: "executed"; summary?: string }
  | { kind: "dismissed" }
  | { kind: "expired" };

/**
 * Confirm-tier action card (spec §5 "humans win"): verb + object +
 * consequence, Confirm/Dismiss. Both disabled while pending; success
 * collapses into a resolved row; 409 collapses into the expired row
 * (the mutation hook already fired the toast).
 */
export function ConfirmationCard({
  messageId,
  confirmationId,
  verb,
  object,
  consequence,
  respond,
  initialStatus = "pending",
  initialSummary,
}: ConfirmationCardProps) {
  const { t } = useTranslation("assistant");

  const [resolution, setResolution] = useState<Resolution | null>(() => {
    if (initialStatus === "confirmed") return { kind: "executed", summary: initialSummary };
    if (initialStatus === "dismissed") return { kind: "dismissed" };
    if (initialStatus === "expired") return { kind: "expired" };
    return null;
  });

  if (resolution?.kind === "expired") {
    return <p className="text-[12px] text-muted-foreground italic">{t("confirmation.expired")}</p>;
  }
  if (resolution?.kind === "dismissed") {
    return <p className="text-[12px] text-muted-foreground italic">{t("confirmation.dismissed")}</p>;
  }
  if (resolution?.kind === "executed") {
    return (
      <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <Check size={12} className="flex-shrink-0 text-green-500" />
        {resolution.summary ?? t("confirmation.done")}
      </p>
    );
  }

  const handle = async (action: "confirm" | "dismiss") => {
    try {
      const result = await respond.mutateAsync({ messageId, confirmationId, action });
      setResolution(
        result.status === "executed"
          ? { kind: "executed", summary: result.summary }
          : { kind: "dismissed" },
      );
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409) {
        setResolution({ kind: "expired" });
      }
      // Other failures: the hook toasts; keep the card so the user can retry.
    }
  };

  return (
    <div className="rounded-lg border border-border bg-surface-0 p-3 space-y-2">
      <p className="text-[13px] leading-snug">
        <span className="font-semibold">{verb}</span>{" "}
        <span className="font-mono text-[12px]">{object}</span>
      </p>
      {consequence && <p className="text-[12px] text-muted-foreground">{consequence}</p>}
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          className="h-7 px-3 text-[12px]"
          disabled={respond.isPending}
          onClick={() => void handle("confirm")}
        >
          {respond.isPending && <Loader2 size={12} className="animate-spin" />}
          {t("confirmation.confirm")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-3 text-[12px]"
          disabled={respond.isPending}
          onClick={() => void handle("dismiss")}
        >
          {t("confirmation.dismiss")}
        </Button>
      </div>
    </div>
  );
}
